import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { environment } from '../../../../../../environments/environment';
import { BoardService } from '../../../../../services/board.service';
import { DialogService } from '../../../../../services/dialog.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { CardTimelineCommentEvent } from '../../../board-v3.types';
import { formatFullName } from '../../../../../helpers';

@Component({
  selector: 'app-board-v3-timeline-comment',
  templateUrl: './timeline-comment.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3TimelineCommentComponent {
  @Input() cardId!: string;
  @Input() event!: CardTimelineCommentEvent;
  /** No edit / delete: the comment is not of a card (e.g. of a task in a user's timeline) */
  @Input() readonly = false;
  @Output() commentChanged = new EventEmitter<void>();

  private readonly boardService = inject(BoardService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  isEditing = false;
  savingComment = false;
  commentControl = new FormControl('');

  getTimelineAuthorName(): string {
    const user = this.event?.created_by_user;
    if (!user) return 'System';

    return formatFullName(user.first_name, user.last_name) || user.email || 'System';
  }

  getTimelineAuthorAvatarUrl(): string | undefined {
    return this.event?.created_by_user?.avatar_url || undefined;
  }

  getCommentHtml(): string | null {
    const comment = this.event?.x_data?.comment;

    return typeof comment === 'string' ? comment : null;
  }

  onEditClick(): void {
    this.commentControl.setValue(this.getCommentHtml() ?? '');
    this.isEditing = true;
  }

  onCancelEdit(): void {
    this.isEditing = false;
  }

  onSaveEdit(): void {
    const text = this.commentControl.value?.trim();
    if (!text || this.savingComment) return;

    this.savingComment = true;
    this.boardService
      .updateComment(this.cardId, this.event.id, { text })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingComment = false;
          this.isEditing = false;
          this.commentChanged.emit();
        },
        error: () => {
          this.savingComment = false;
          this.notificationService.showError('', 'Failed to update comment');
        },
      });
  }

  onDeleteClick(): void {
    this.dialogService
      .openConfirm({ text: 'timeline_comment_delete_confirm' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.boardService
          .deleteComment(this.cardId, this.event.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => this.commentChanged.emit(),
            error: () => this.notificationService.showError('', 'Failed to delete comment'),
          });
      });
  }
}
